import * as Sentry from "@sentry/react";
import type { ColumnDef } from "@tanstack/react-table";
import { Timestamp } from "firebase/firestore";
import { KeyRound, MoreHorizontal, Pencil, Plus, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { ListSearch, ListToolbar } from "@/components/ui/list-toolbar";
import { PageHeader } from "@/components/ui/page-header";

import { getFirebaseUsers } from "../../../api/OpenAIApi";
import BetaAccessModal from "../../../components/betaAccessModal";

interface User {
  uid: string;
  email: string;
  phoneNumber: string;
  role: string;
  firstName: string;
  lastName: string;
  lawFirmName: string;
  disabled: boolean;
  emailVerified: boolean;
  betaFeatures: string[];
  lastSignedInAt: Timestamp;
  createdAt: Timestamp;
}

export const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [tokens, setTokens] = useState<string[]>([]);
  const [tokenIndex, setTokenIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserName, setSelectedUserName] = useState<string>("");
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(
    undefined
  );
  const [showBetaModal, setShowBetaModal] = useState<boolean>(false);

  const perPage = 25;

  const getUserData = async (
    orderBy = "",
    uid = "",
    email = "",
    maxResults = perPage,
    pageToken = ""
  ) => {
    setIsLoading(true);
    try {
      if (pageToken === "") {
        setTokenIndex(0);
      }
      const usersData = await getFirebaseUsers(
        orderBy,
        uid,
        email,
        maxResults,
        pageToken
      );
      setUsers(usersData.users);
      if (
        usersData.nextPageToken &&
        !tokens.includes(usersData.nextPageToken)
      ) {
        setTokens([...tokens, usersData.nextPageToken]);
      }
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      setIsLoading(false);
    }
  };

  const changePage = (destination: "previous" | "next") => {
    const nextIndex =
      destination === "previous" ? tokenIndex - 1 : tokenIndex + 1;
    setTokenIndex(nextIndex);
    getUserData("", "", searchQuery, perPage, tokens[tokenIndex]);
  };

  useEffect(() => {
    getUserData();
  }, []);

  const handleSearch = () => {
    setTokens([]);
    getUserData("", "", searchQuery);
  };

  const handleBetaAccess = (user: User) => {
    setSelectedUserId(user.uid);
    setSelectedUserName(
      user.firstName ? `${user.firstName} ${user.lastName}` : user.email
    );
    setShowBetaModal(true);
  };

  const editPath = (user: User) => `/admin/users/${user.uid}/edit`;

  const columns: ColumnDef<User, unknown>[] = [
    {
      id: "user",
      header: "User",
      cell: ({ row }) => {
        const u = row.original;
        const name = [u.firstName, u.lastName].filter(Boolean).join(" ");
        return (
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-base text-fg">
              {name || u.email}
            </span>
            {name ? (
              <span className="truncate text-sm text-fg-3">{u.email}</span>
            ) : null}
          </div>
        );
      }
    },
    {
      id: "role",
      header: "Role",
      cell: ({ row }) => (
        <span className="text-base text-fg-2">{row.original.role || "—"}</span>
      )
    },
    {
      id: "firm",
      header: "Law Firm",
      cell: ({ row }) => (
        <span className="truncate text-base text-fg-2">
          {row.original.lawFirmName || "—"}
        </span>
      )
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Options">
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={editPath(row.original)}>
                  <Pencil />
                  Edit
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => handleBetaAccess(row.original)}>
                <KeyRound />
                Beta Access
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )
    }
  ];

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-surface">
      <div className="flex shrink-0 flex-col gap-4 px-6 pt-6 pb-3">
        <PageHeader
          title="Users"
          actions={
            <Button size="sm" asChild>
              <Link to="/admin/users/new">
                <Plus /> New User
              </Link>
            </Button>
          }
        />
        <ListToolbar>
          <ListSearch
            aria-label="Search users"
            placeholder="Search by Email"
            value={searchQuery}
            onValueChange={setSearchQuery}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />
          <Button variant="secondary" size="sm" onClick={handleSearch}>
            Search
          </Button>
        </ListToolbar>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {!isLoading && users.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No Users Found"
            description="Use the system to add users."
            className="py-16"
          />
        ) : (
          <DataTable
            data={users}
            columns={columns}
            rowHeight="double"
            getRowId={(u) => u.uid}
            onRowClick={(u) => navigate(editPath(u))}
            isLoading={isLoading}
            className="overflow-visible"
          />
        )}
        <div className="flex items-center justify-center gap-2 px-6 py-4">
          <Button
            variant="secondary"
            size="sm"
            disabled={tokenIndex === 0 || isLoading}
            onClick={() => changePage("previous")}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={tokenIndex === tokens.length || isLoading}
            onClick={() => changePage("next")}
          >
            Next
          </Button>
        </div>
      </div>

      <BetaAccessModal
        userId={selectedUserId}
        userName={selectedUserName}
        isOpen={showBetaModal}
        closeModal={() => setShowBetaModal(false)}
      />
    </div>
  );
};
